import type { RouteLocationRaw } from 'vue-router';
import type { ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed } from 'vue';
import { useRoute } from '#app/composables/router';
import { canEditDataModel, useEntitlement, usePermissionsStore } from '#layers/access/public';
import { getCanonicalCollectionRoute, useSchemaBasedOnRoute, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { CONTENT_COLLECTION_ROUTE_NAME, CONTENT_ROUTE_NAME, DATA_MODEL_ROUTE_NAME, useContentPages } from '#layers/spaces/public';

interface ModeRoute {
	name: string;
	params: Record<string, string>;
}

function toModeRoute(target: RouteLocationRaw | undefined, fallbackName: string): ModeRoute {
	if (target && typeof target === 'object' && 'name' in target && target.name) {
		return {
			name: String(target.name),
			params: (('params' in target && target.params) || {}) as Record<string, string>,
		};
	}

	return { name: fallbackName, params: {} };
}

export function useActiveCollection() {
	const route = useRoute();
	const { collection: dataModelCollection } = useSchemaBasedOnRoute();
	const { findCollectionByName } = useWorkspaceSchemaStore();
	const { visibleContentPages } = useContentPages();
	const { canRead } = usePermissionsStore();
	const { can } = useEntitlement();

	const activeCollection = computed<ResolvedMonospaceCollection | undefined>(() => {
		if (dataModelCollection.value) return dataModelCollection.value;
		if (isInMode(DATA_MODEL_ROUTE_NAME)) return;

		const collectionParam = route.params.collection;
		return typeof collectionParam === 'string' ? findCollectionByName(collectionParam) : undefined;
	});

	const contentModeRoute = computed<ModeRoute>(() => {
		const collection = activeCollection.value;
		const ownPage = collection && canRead(collection.id)
			? visibleContentPages.value.find((entry) => entry.collectionId === collection.id)
			: undefined;
		const page = ownPage ?? visibleContentPages.value[0];

		return page
			? { name: CONTENT_COLLECTION_ROUTE_NAME, params: { collection: page.route } }
			: { name: CONTENT_ROUTE_NAME, params: {} as Record<string, string> };
	});

	const dataModelModeRoute = computed(() => toModeRoute(
		activeCollection.value && can(canEditDataModel) ? getCanonicalCollectionRoute(activeCollection.value) : undefined,
		DATA_MODEL_ROUTE_NAME,
	));

	function isInMode(rootName: string): boolean {
		const name = String(route.name ?? '');
		return name === rootName || name.startsWith(`${rootName}.`);
	}

	const isContentActive = computed(() => isInMode(CONTENT_ROUTE_NAME));
	const isDataModelActive = computed(() => isInMode(DATA_MODEL_ROUTE_NAME));

	return { activeCollection, contentModeRoute, dataModelModeRoute, isContentActive, isDataModelActive };
}
